import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/cn.js';
/**
 * DESIGN_SYSTEM.md §6 #1. States: default, hover, focus, active, disabled, loading.
 *
 * `loading` replaces the label with a spinner in the SAME color family and DISABLES the
 * button itself — this is the double-submission guard ux-review names explicitly. It is
 * not decorative: the underlying `disabled` attribute is set whenever `loading` is true,
 * regardless of what the `disabled` prop says.
 *
 * §2.2: never a fixed pixel width — `min-width` + intrinsic sizing only, so a ~2.2x
 * longer Nepali label wraps to a second line rather than clipping.
 */
const buttonVariants = cva([
    'inline-flex min-w-[6rem] min-h-touch items-center justify-center gap-2 rounded-md px-4 py-2',
    'text-sm font-semibold transition-colors duration-fast',
    'focus-visible:outline-none focus-visible:shadow-focus',
    'disabled:cursor-not-allowed disabled:opacity-60',
], {
    variants: {
        variant: {
            primary: 'bg-brand-fill text-text-onprimary hover:bg-brand-fill-hover active:bg-brand-fill-hover disabled:bg-brand-fill-disabled',
            secondary: 'border border-border-functional bg-surface-base text-text-primary hover:bg-surface-hover active:bg-surface-hover disabled:border-neutral-200 disabled:text-text-disabled',
            ghost: 'text-brand-fill hover:bg-surface-hover active:bg-surface-hover disabled:text-text-disabled',
            destructive: 'bg-destructive-fill text-text-onprimary hover:bg-destructive-fill-hover active:bg-destructive-fill-hover disabled:bg-brand-fill-disabled',
        },
        size: {
            md: 'text-sm',
            sm: 'min-h-0 px-3 py-1.5 text-sm',
        },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
});
export const Button = React.forwardRef(function Button({ className, variant, size, asChild, loading = false, loadingLabel = 'Loading', disabled, children, ...props }, ref) {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled === true || loading;
    return (_jsx(Comp, { ref: ref, type: asChild ? undefined : (props.type ?? 'button'), className: cn(buttonVariants({ variant, size }), className), disabled: isDisabled, "aria-busy": loading || undefined, "aria-disabled": isDisabled || undefined, ...props, children: loading ? (_jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", className: "h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" }), _jsx("span", { className: "sr-only", children: loadingLabel })] })) : (children) }));
});
//# sourceMappingURL=Button.js.map